import { isConnectedAccount } from 'owa-accounts-store';
import type AttendeeType from 'owa-service/lib/contract/AttendeeType';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { now, differenceInCalendarDays } from 'owa-datetime';
import getConflictCount from 'owa-calendar-forms-common/lib/utils/getConflictCount';
import getMetricLocationType from 'owa-calendar-forms-utils/lib/utils/getMetricLocationType';
import getQuickComposeControlUsageMetrics from './getQuickComposeControlUsageMetrics';
import { getComposeStore } from 'owa-calendar-compose-store-manager';
import type { MetricsInfo } from 'owa-calendar-compose-types';
import type {
    ComposeFormType,
    QuickComposeViewType,
    FullComposeFormViewType,
} from 'owa-calendar-helpers-types/lib/ComposeTypes';
import { getRecurrenceMetricString } from 'owa-recurrence-utils/lib/checkRecurrenceTypes';
import { isEventAMeeting, isOnGroupCalendar } from 'owa-calendar-event-capabilities';
import type { LocationMetricType } from 'owa-calendar-helpers-types/lib/LocationMetricTypes';
import type { RecurrenceNameStringType } from 'owa-recurrence-utils/lib/constants';
import type {
    QuickComposeEntrySourceType,
    FullComposeEntrySourceType,
    EventCompletionStatusType,
} from 'owa-calendar-helpers-types/lib/ComposeMetricTypes';
import { logInboxReminderOffsetsForCompose } from 'owa-email-reminders';
import getEventDiffMetricProperties from './getEventDiffMetricProperties';
import isRecipientDistributionList from './isRecipientDistributionList';
import { isFeatureEnabled } from 'owa-feature-flags';
import getRecurrenceRoomsAvailabilitiesState from 'owa-room-selection/lib/utils/getRecurrenceRoomsAvailabilitiesState';
import { timeOfDay } from 'owa-datetime-utils';
import getFormOpenDuration from './getFormOpenDuration';
import getCalendarComponent from './getCalendarComponent';

function hasGivenAttendee(attendees: AttendeeType[], smtpAddress: string): boolean {
    return attendees.filter(attendee => attendee.Mailbox.EmailAddress == smtpAddress).length > 0;
}

function getGroupAndDLCount(attendees: AttendeeType[]): number {
    if (!attendees) {
        return 0;
    }

    return attendees.filter(attendee => {
        return isRecipientDistributionList(attendee);
    }).length;
}

export default function getEventMetricProperties(
    diffEvent: CalendarEvent, // The event that contains only the properties that have been changed
    quickComposeDiffEvent: CalendarEvent, // The event that contains only the changes in Quick Form before user switches to Full Form
    fullComposeDiffEvent: CalendarEvent, // The event that contains only the changes in Full Form after user switched from Quick Form
    event: CalendarEvent, // The event to be saved - include all the values that are set on diffEvent
    eventCompletionType: EventCompletionStatusType,
    formType: ComposeFormType,
    viewType: QuickComposeViewType | FullComposeFormViewType,
    quickComposeEntrySource: QuickComposeEntrySourceType,
    metricsInfo: MetricsInfo,
    fullComposeEntrySource?: FullComposeEntrySourceType
) {
    const { originalEvent, uiState: viewState } = getComposeStore();

    // If Id is not set, then it is a new event scenario.
    // If Id is set, but if it was a draft that we are making it a non-draft, then it is a new event
    const isNewEvent: boolean =
        !event.ItemId || !event.ItemId.Id || (originalEvent?.IsDraft && !event.IsDraft);

    const eventDiffMetricProperties = getEventDiffMetricProperties(
        originalEvent,
        diffEvent,
        isNewEvent,
        null /*prefix*/
    );

    const metricPropertiesInQuickCompose = quickComposeDiffEvent
        ? getEventDiffMetricProperties(originalEvent, quickComposeDiffEvent, isNewEvent, 'QC_')
        : {};

    const metricPropertiesInFullForm = fullComposeDiffEvent
        ? getEventDiffMetricProperties(
              metricsInfo.quickComposeFinalEvent,
              fullComposeDiffEvent,
              isNewEvent,
              'FC_'
          )
        : {};

    const quickComposeToFullComposeEntry = metricsInfo.quickComposeToFullComposeEntry;
    const quickComposePivotType = metricsInfo.quickComposePivotType;

    const subjectLength = event.Subject ? event.Subject.length : 0;
    const recurrenceType: RecurrenceNameStringType = getRecurrenceMetricString(event.Recurrence);

    let quickComposeLocation = diffEvent.Locations ? diffEvent.Locations[0] : null;
    let QClocationType: LocationMetricType = getMetricLocationType(quickComposeLocation);
    const numLocations = event.Locations ? event.Locations.length : 0;
    const numResources = event.Resources ? event.Resources.length : 0;

    const start = event.Start;
    const end = event.End;

    // Can count number of resources busy if have FB cache initialized.
    // Either quick or full compose initialize the cache. If not initialized, then is not the scenario that changes meeting schedule, no need to count.
    let numResourcesBusy: number =
        event.Resources && viewState.freeBusyData
            ? getConflictCount(event.Resources, start, end, originalEvent)
            : 0;

    // Log recurrence room availabilities, e.g. X available out of Y instances
    let recurrenceRoomAvailabilityMetric = {};
    if (event.Recurrence && event.Resources && event.Resources.length > 0) {
        const { totalInstances, freeInstancesCount } = getRecurrenceRoomsAvailabilitiesState(event);

        recurrenceRoomAvailabilityMetric['TotalInstances'] = totalInstances;
        recurrenceRoomAvailabilityMetric['FreeInstancesCount'] = freeInstancesCount;
    }

    const daysTillEvent = differenceInCalendarDays(start, now());

    const isMeeting = isEventAMeeting(event);
    const isGroupMailbox = isOnGroupCalendar(event);

    const numRequiredAttendees = event.RequiredAttendees ? event.RequiredAttendees.length : 0;
    const numOptionalAttendees = event.OptionalAttendees ? event.OptionalAttendees.length : 0;
    const numRequiredDLs = getGroupAndDLCount(event.RequiredAttendees);
    const numOptionalDLs = getGroupAndDLCount(event.OptionalAttendees);

    const hasGroupAsAttendee =
        isGroupMailbox &&
        isMeeting &&
        ((numRequiredAttendees > 0 &&
            hasGivenAttendee(
                event.RequiredAttendees,
                event.ParentFolderId.mailboxInfo.mailboxSmtpAddress
            )) ||
            (numOptionalAttendees > 0 &&
                hasGivenAttendee(
                    event.OptionalAttendees,
                    event.ParentFolderId.mailboxInfo.mailboxSmtpAddress
                )));

    const attachmentsChanged = diffEvent.Attachments != undefined && diffEvent.Attachments.length;
    const categoryChanged = diffEvent.Categories != null && diffEvent.Categories.length;

    const automatedRoomBookingEnabled =
        isFeatureEnabled('cal-mf-roomBookingAssistant') &&
        !isConnectedAccount(event.ParentFolderId.mailboxInfo.userIdentity)
            ? event.IsRoomRequested
            : undefined;

    const qcControlUsageMetrics = getQuickComposeControlUsageMetrics(metricsInfo);

    // inbox reminder properties
    const numOfInboxReminders = event.InboxReminders ? event.InboxReminders.length : 0;
    const inboxRemindersChanged = !!diffEvent.InboxReminders;
    if (inboxRemindersChanged) {
        logInboxReminderOffsetsForCompose(event.InboxReminders, originalEvent.InboxReminders);
    }

    const formOpenDuration = getFormOpenDuration();

    const component = getCalendarComponent();

    return {
        FormSessionId: metricsInfo.formSessionId,
        EventCompletionType: eventCompletionType,
        Component: component,
        EntrySource: quickComposeEntrySource || fullComposeEntrySource,
        QuickComposeToFullComposeEntry: quickComposeToFullComposeEntry,
        QuickComposePivotType: quickComposePivotType,
        IsMeeting: isMeeting,
        IsNewEvent: isNewEvent,
        SubjectLength: subjectLength,
        RecurrenceType: recurrenceType,
        NumInboxReminders: numOfInboxReminders,
        InboxRemindersChanged: inboxRemindersChanged,
        NumRequiredAttendees: numRequiredAttendees,
        NumRequiredDLs: numRequiredDLs,
        NumOptionalAttendes: numOptionalAttendees,
        NumOptionalDLs: numOptionalDLs,
        NumLocations: numLocations,
        NumResources: numResources,
        NumResourcesBusy: numResourcesBusy,
        AttachmentsChanged: attachmentsChanged,
        CategoryChanged: categoryChanged,
        QClocationType: QClocationType,
        FullComposeEntrySource: fullComposeEntrySource,
        FormType: formType,
        ViewType: viewType,
        TimeInQC: metricsInfo.timeInQuickCompose,
        TimeInFC: metricsInfo.timeInFullCompose,
        TimeInSA: metricsInfo.timeInSchedulingAssistant,
        TimeInRP: metricsInfo.timeInRoomBookingPanel,
        FormOpenDuration: formOpenDuration,
        SchedulingGridUsed: metricsInfo.schedulingGridUsed,
        IsGroupMailbox: isGroupMailbox,
        HasGroupAsAttendee: hasGroupAsAttendee,
        CalendarItemType: event.CalendarItemType,
        DaysTillEvent: daysTillEvent,
        AutomatedRoomBookingEnabled: automatedRoomBookingEnabled,
        ...qcControlUsageMetrics,
        ...eventDiffMetricProperties,
        ...metricPropertiesInQuickCompose,
        ...metricPropertiesInFullForm,
        ...recurrenceRoomAvailabilityMetric,
        TimeOfDayActionTaken: timeOfDay(),
    };
}
